import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';

import i18n from 'util/i18n';
import { getErrorCode } from 'util/errors';

import { saveRequest } from '@redux/modules/save/actions';
import { rootSelectors } from '@redux/modules/selectors';
import SvgComponent from 'components/Form/SvgComponent';

class SavedIndicator extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      interval: props.interval || 1000,
      elapsed: props.elapsed || 0,
      hover: false,
    };
  }

  componentDidMount() {
    const { interval } = this.state;
    this.timer = window.setInterval(this.tick, interval);
  }

  UNSAFE_componentWillReceiveProps(nextProps) { // eslint-disable-line react/sort-comp
    this.tick(nextProps);
  }

  componentWillUnmount() {
    window.clearInterval(this.timer);
  }

  isRoute = (route) => (window.location.pathname || '').includes(route);

  save = () => {
    const { sectionKey, subsectionKey, dispatch } = this.props;

    // So, this is fun, it was determined the save button is not required on
    // `form/package/print` but should still be available on any other section
    // found in "Review and submit". The crux is saving within this section does
    // nothing since it was declared signatures should **only** be saved when
    // the form is **submitted**.
    //
    // So what we have here is a reset of the elapsed time when the save button
    // is clicked within the "Review and submit" section to provide comfort to
    // the end user.
    if (this.isRoute('form/package') && !this.isRoute('form/package/comments')) {
      this.setState({ elapsed: 0 });
      return;
    }

    dispatch(saveRequest(sectionKey, subsectionKey));
  };

  mouseEnter = () => {
    this.setState({ hover: true });
  };

  mouseLeave = () => {
    this.setState({ hover: false });
  };

  tick = (props = this.props) => {
    const { saved } = props;
    const currentTick = new Date().getTime();
    let s = currentTick;
    if (props && saved) {
      s = saved.getTime();
    }

    this.setState({ elapsed: currentTick - s });
  };

  calculateTime = () => {
    const { elapsed } = this.state;

    // If there has been no time elapsed...
    if (!elapsed) {
      return i18n.t('saved.now');
    }

    // Get the time in seconds
    let timespan = elapsed / 1000;

    // Determine the unit of measurement and then adjust the timespan
    // rounding the value
    let unit = '';
    if (timespan / (24 * 60 * 60) >= 1) {
      timespan /= 24 * 60 * 60;
      timespan = Math.round(timespan);
      unit = timespan > 1 ? i18n.t('saved.days') : i18n.t('saved.day');
    } else if (timespan / (60 * 60) >= 1) {
      timespan /= 60 * 60;
      timespan = Math.round(timespan);
      unit = timespan > 1 ? i18n.t('saved.hours') : i18n.t('saved.hour');
    } else if (timespan / 60 >= 1) {
      timespan /= 60;
      timespan = Math.round(timespan);
      unit = timespan > 1 ? i18n.t('saved.minutes') : i18n.t('saved.minute');
    } else {
      timespan = Math.round(timespan);
      if (timespan < 1) {
        return i18n.t('saved.now');
      }
      unit = timespan === 1 ? i18n.t('saved.second') : i18n.t('saved.seconds');
    }

    return `${timespan} ${unit} ${i18n.t('saved.ago')}`;
  };

  allowed = () => !this.props.formIsLocked && !this.isRoute('form/package/print');

  render() {
    if (!this.allowed()) {
      return null;
    }

    const { hasError } = this.props;
    const { hover, elapsed } = this.state;
    const animate = this.props.isSaving;

    const buttonClasses = classnames('saved-indicator', {
      active: animate,
      error: !animate && hasError,
    });

    const circleClasses = classnames('spinner-icon', { spin: animate });
    const iconClasses = classnames('save-icon', animate ? 'invert' : 'idle', { invert: animate });

    // Determine the appropriate response for screen readers.
    let talkback = null;
    if (!animate) {
      if (hasError) {
        talkback = i18n.t('saved.error.message');
      } else if (hover) {
        talkback = i18n.t('saved.action');
      } else if (elapsed && elapsed / 1000 < 60) {
        // Less than a minute ago.
        talkback = i18n.t('saved.recently');
      } else {
        talkback = `${i18n.t('saved.saved')} ${this.calculateTime()}. ${i18n.t('saved.action')}?`;
      }
    } else {
      talkback = i18n.t('saved.saving');
    }
    const errorTimeSting = this.props.errorTime?.toISOString().substring(5, 19);
    return (
      <div className="saved-indicator-container">
        <button
          className={buttonClasses}
          aria-label={talkback}
          title={talkback}
          onClick={this.save}
          onMouseEnter={this.mouseEnter}
          onMouseLeave={this.mouseLeave}
          type="button"
        >
          <div className="spinner">
            <div className={circleClasses} />
            <div className={iconClasses}>
              <SvgComponent className={iconClasses} src="/img/save.svg" />
            </div>
          </div>

          <span className="spinner-label">
            {(animate || (hover && !hasError)) && (
              <strong className="one-line">
                {animate ? i18n.t('saved.saving') : i18n.t('saved.action')}
              </strong>
            )}

            {!animate && !hover && !hasError && (
              <span>
                <strong>{i18n.t('saved.saved')}</strong>
                <span className="time">{this.calculateTime()}</span>
              </span>
            )}

            {!animate && hasError && (
              <span>
                <strong>{i18n.t('saved.error.title')}</strong>
                <span className="time">{i18n.t('saved.error.subtitle')}<br />Code: {getErrorCode(this.props.error).code.toString()}<br />{errorTimeSting}</span>
              </span>
            )}
          </span>
        </button >
      </div >
    );
  }
}

SavedIndicator.propTypes = {
  interval: PropTypes.number,
  elapsed: PropTypes.number,
  saveError: PropTypes.any,
  error: PropTypes.any,
  errorTime: PropTypes.any,
  hasError: PropTypes.bool,
  saved: PropTypes.object,
  sectionKey: PropTypes.string,
  subsectionKey: PropTypes.string,
  dispatch: PropTypes.func,
  formIsLocked: PropTypes.bool,
  isSaving: PropTypes.bool,
};

SavedIndicator.defaultProps = {
  interval: 1000,
  elapsed: 0,
  saveError: null,
  error: null,
  errorTime: null,
  hasError: false,
  saved: {},
  sectionKey: null,
  subsectionKey: null,
  formIsLocked: false,
  dispatch: () => { },
};

/**
 * @param {import('types/state').RootState} state
 */
const mapStateToProps = (state) => ({
  formIsLocked: rootSelectors.application.getIsLocked(state),
  ...state.save,
  ...rootSelectors.section.getSectionKeys(state),
});

export { SavedIndicator };

export default connect(mapStateToProps)(SavedIndicator);
